import { useQuery } from '@tanstack/react-query';
import {
  getSalesReport, getRecentSales, getPurchasesReport, getPurchaseArtists, getPurchaseShows, getPurchaseListings,
  getPurchaseLines, getPurchaseBuyers,
} from '../api';

// Reports page (super admin only). Local data on the server; while the range
// includes today, the sales report and Most recent sales first run the
// server's Skybox invoice check (at most one a minute) and carry its outcome
// as `live`. Purchases and the purchases tables are local data only.
// Keys hold every param, so a new range or page is a new query; the
// previous answer stays on screen (dimmed) until the new one lands.
export const reportKeys = {
  all: ['reports'],
  sales: (p) => ['reports', 'sales', p],
  // p: the range, limit and offset
  recentSales: (p) => ['reports', 'recentSales', p],
  purchases: (p) => ['reports', 'purchases', p],
  // p: treeParams plus offset
  purchaseArtists: (p) => ['reports', 'purchaseArtists', p],
  // p: showsParams (the range and the artist's key)
  purchaseShows: (p) => ['reports', 'purchaseShows', p],
  // p: listingsParams (the range, the artist's and the show's keys, the offset)
  purchaseListings: (p) => ['reports', 'purchaseListings', p],
  // p: the range and offset, plus buyer (a buyers row's key) for an open buyer
  purchaseLines: (p) => ['reports', 'purchaseLines', p],
  // p: the range
  purchaseBuyers: (p) => ['reports', 'purchaseBuyers', p],
};

const REPORT_STALE_MS = 60 * 1000;
// Only while the range covers today and is at most 62 days long (`live`,
// isLiveRange); React Query skips interval refetches in a hidden tab, so a
// background tab never polls
const LIVE_REFRESH_MS = 60 * 1000;

const hasRange = (p) => !!(p?.dateFrom && p?.dateTo);

function reportQuery(queryKey, fetch, enabled, live) {
  return {
    queryKey,
    queryFn: async () => (await fetch()).data,
    enabled,
    staleTime: REPORT_STALE_MS,
    placeholderData: (prev) => prev,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  };
}

export function useSalesReport(params, live) {
  return useQuery(reportQuery(reportKeys.sales(params), () => getSalesReport(params), hasRange(params), live));
}

export function usePurchasesReport(params, live) {
  return useQuery(reportQuery(reportKeys.purchases(params), () => getPurchasesReport(params), hasRange(params), live));
}

// The previous period for the tiles' changes: the same
// report over its own params (so the same cache), only when the caller asks
// (`enabled`), and never polled: a previous period ends before today, so it
// is settled and the server runs no Skybox check for it
export function usePreviousSalesReport(params, enabled) {
  return useQuery(reportQuery(reportKeys.sales(params), () => getSalesReport(params), !!enabled && hasRange(params), false));
}

export function usePreviousPurchasesReport(params, enabled) {
  return useQuery(reportQuery(reportKeys.purchases(params), () => getPurchasesReport(params), !!enabled && hasRange(params), false));
}

// The purchases table: the artists (params: treeParams plus offset), an open
// artist's shows and an open show's listings (`enabled`: the caller has
// the keys to ask with)
export function usePurchaseArtists(params, live) {
  return useQuery(reportQuery(reportKeys.purchaseArtists(params), () => getPurchaseArtists(params), hasRange(params), live));
}

export function usePurchaseShows(params, live, enabled) {
  return useQuery(reportQuery(
    reportKeys.purchaseShows(params),
    () => getPurchaseShows(params),
    !!enabled && hasRange(params),
    live,
  ));
}

export function usePurchaseListings(params, live, enabled) {
  return useQuery(reportQuery(
    reportKeys.purchaseListings(params),
    () => getPurchaseListings(params),
    !!enabled && hasRange(params),
    live,
  ));
}

// Most recent purchases and an open buyer's lines (params: the range, offset
// and, for a buyer, buyer), and Purchases by tag's buyers (params: the range)
export function usePurchaseLines(params, live, enabled) {
  return useQuery(reportQuery(
    reportKeys.purchaseLines(params),
    () => getPurchaseLines(params),
    !!enabled && hasRange(params),
    live,
  ));
}

export function usePurchaseBuyers(params, live) {
  return useQuery(reportQuery(reportKeys.purchaseBuyers(params), () => getPurchaseBuyers(params), hasRange(params), live));
}

// Most recent sales (params: the range, limit and offset): a page at a time,
// the last page kept on screen (dimmed) until the next lands
export function useRecentSales(params, live) {
  return useQuery(reportQuery(reportKeys.recentSales(params), () => getRecentSales(params), hasRange(params), live));
}
